// Composite aero/inlet database API client.
//
// Read-side client for thread-api's /api/composite endpoints: the
// multi-source (ROM/CFD/WT/FT) operating-condition database with
// trust policies, applicability, corrections, and point-level
// provenance. Source identity is provenance carried on values — the
// quantity names themselves are always source-neutral.

import { apiClient } from "./client";

export type SourceClass = "rom" | "cfd" | "wind_tunnel" | "flight_test" | "manual";

export type CompositeClassification =
  | "ok"
  | "low_confidence"
  | "conflicting"
  | "extrapolation"
  | "no_data";

export interface CompositeSource {
  artifact_id: string;
  source_key: string;
  name: string;
  source_class: SourceClass;
  tool: string | null;
  n_rows: number;
  match_tolerances: Record<string, number>;
  created_by: string | null;
  created_at: string | null;
}

export interface CompositeCorrectionSummary {
  correction_id: string;
  dataset_id: string;
  condition_key: string;
  quantity: string;
  action: "replace" | "reject";
  reason: string;
  author: string;
  status: string;
  artifact_id: string | null;
}

export interface CompositeAttachment {
  artifact_id: string;
  kind: string;
  type: string;
  title?: string | null;
  summary: string;
  channel?: string | null;
  permalink?: string | null;
  file?: string | null;
  slide?: number | null;
  attached_to: { kind: string; name: string };
  created_by?: string | null;
}

export interface CompositeSourcesResponse {
  branch: string;
  sources: CompositeSource[];
  policies: string[];
  buildups?: string[];
  attachments?: CompositeAttachment[];
  corrections: CompositeCorrectionSummary[];
}

export interface CompositeValue {
  value: number | null;
  sigma: number | null;
  source_class: SourceClass | null;
  dataset_name: string | null;
  provenance?: Record<string, unknown>;
  classification: CompositeClassification;
  corrected: boolean;
}

export interface ConditionViewRow {
  condition_key: string;
  condition: Record<string, number | string>;
  values: Record<string, CompositeValue>;
}

export interface ConditionViewResponse {
  policy: string;
  scope: string;
  total: number;
  offset: number;
  rows: ConditionViewRow[];
}

export interface ComparisonSourceCell {
  source_class: SourceClass;
  value: number | null;
  raw_value: number | null;
  sigma: number | null;
  corrected: boolean;
  rejected: boolean;
  exact: boolean;
  distance: number;
  applicability: string;
}

export interface ComparisonRow {
  condition_key: string;
  condition: Record<string, number | string>;
  sources: Record<string, ComparisonSourceCell>;
  composite: {
    value: number | null;
    source_class: SourceClass | null;
    dataset_name: string | null;
    classification: CompositeClassification;
    corrected: boolean;
  };
  disagreement: { z: number; a?: { dataset: string; value: number }; b?: { dataset: string; value: number } };
}

export interface ComparisonResponse {
  policy: string;
  quantity: string;
  scope: string;
  total: number;
  offset: number;
  rows: ComparisonRow[];
}

export interface ExplainCandidate {
  dataset_id: string;
  dataset_name: string;
  source_class: SourceClass;
  tool: string | null;
  value: number | null;
  raw_value: number | null;
  sigma: number | null;
  corrected: boolean;
  rejected: boolean;
  corrections: Array<{
    correction_id: string;
    action: string;
    status: string;
    reason: string;
    author: string;
    original_value: number | null;
    corrected_value: number | null;
    artifact_id: string | null;
  }>;
  match: {
    condition_key: string;
    deltas: Record<string, number>;
    distance: number;
    exact: boolean;
  };
  applicability: {
    status: string;
    matched_regions: Array<{ kind: string; note: string; artifact_id: string | null }>;
    declared: boolean;
  };
  provenance: Record<string, unknown>;
}

export interface ExplainResponse {
  condition_key: string;
  condition: Record<string, number | string>;
  quantity: string;
  policy: string;
  value: number | null;
  sigma: number | null;
  source_class: SourceClass | null;
  dataset_id: string | null;
  dataset_name: string | null;
  provenance?: Record<string, unknown>;
  corrected: boolean;
  classification: CompositeClassification;
  fired_rule: {
    index: number;
    source_class: SourceClass;
    min_applicability: string;
    note: string;
  } | null;
  skipped: Array<{ dataset: string; rule_index: number; why: string }>;
  candidates: ExplainCandidate[];
  disagreement: ComparisonRow["disagreement"];
}

export interface TrustMapCell {
  [axis: string]: number | string | null;
}

export interface TrustMapResponse {
  quantity: string;
  policy: string;
  axis_x: string;
  axis_y: string;
  x_bounds: [number, number];
  y_bounds: [number, number];
  nx: number;
  ny: number;
  fixed: Record<string, number | string>;
  cells: Array<{
    value: number | null;
    source_class: SourceClass | null;
    classification: CompositeClassification;
    disagreement_z: number;
    [axis: string]: number | string | null;
  }>;
}

export interface CustomPolicyPayload {
  name?: string;
  description?: string;
  conflict_z?: number;
  allow_extrapolation?: boolean;
  rules: Array<{
    source_class: SourceClass;
    min_applicability?: "calibrated" | "valid" | "data_hull";
    note?: string;
  }>;
}

interface CommonParams {
  projectId: string;
  branch?: string;
  policy?: string;
}

function baseParams({ projectId, branch = "main", policy }: CommonParams): URLSearchParams {
  const params = new URLSearchParams({ project_id: projectId, branch });
  if (policy) params.set("policy", policy);
  return params;
}

export async function fetchCompositeSources(
  opts: Omit<CommonParams, "policy">,
): Promise<CompositeSourcesResponse> {
  const params = baseParams(opts);
  return apiClient<CompositeSourcesResponse>(`/composite/sources?${params.toString()}`);
}

export async function fetchConditionView(
  opts: CommonParams & {
    quantities?: string[];
    scope?: "evidence" | "all";
    limit?: number;
    offset?: number;
  },
): Promise<ConditionViewResponse> {
  const params = baseParams(opts);
  if (opts.quantities?.length) params.set("quantities", opts.quantities.join(","));
  if (opts.scope) params.set("scope", opts.scope);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  return apiClient<ConditionViewResponse>(`/composite/condition-view?${params.toString()}`);
}

export async function fetchComparison(
  opts: CommonParams & {
    quantity: string;
    scope?: "evidence" | "all";
    limit?: number;
    offset?: number;
  },
): Promise<ComparisonResponse> {
  const params = baseParams(opts);
  params.set("quantity", opts.quantity);
  if (opts.scope) params.set("scope", opts.scope);
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  return apiClient<ComparisonResponse>(`/composite/comparison?${params.toString()}`);
}

// "Change the policy and watch the composite update": the custom policy
// travels in the body and nothing is persisted server-side.
export async function fetchComparisonWithCustomPolicy(
  opts: Omit<CommonParams, "policy"> & {
    quantity: string;
    policy: CustomPolicyPayload;
    scope?: "evidence" | "all";
  },
): Promise<ComparisonResponse> {
  const params = baseParams({ projectId: opts.projectId, branch: opts.branch });
  params.set("quantity", opts.quantity);
  if (opts.scope) params.set("scope", opts.scope);
  return apiClient<ComparisonResponse>(`/composite/comparison?${params.toString()}`, {
    method: "POST",
    body: { policy: opts.policy },
  });
}

export async function fetchExplain(
  opts: CommonParams & { conditionKey: string; quantity: string },
): Promise<ExplainResponse> {
  const params = baseParams(opts);
  params.set("condition_key", opts.conditionKey);
  params.set("quantity", opts.quantity);
  return apiClient<ExplainResponse>(`/composite/explain?${params.toString()}`);
}

export async function fetchTrustMap(
  opts: CommonParams & {
    quantity: string;
    axisX?: string;
    axisY?: string;
    nx?: number;
    ny?: number;
    fixed?: Record<string, number | string>;
  },
): Promise<TrustMapResponse> {
  const params = baseParams(opts);
  params.set("quantity", opts.quantity);
  if (opts.axisX) params.set("axis_x", opts.axisX);
  if (opts.axisY) params.set("axis_y", opts.axisY);
  if (opts.nx != null) params.set("nx", String(opts.nx));
  if (opts.ny != null) params.set("ny", String(opts.ny));
  if (opts.fixed && Object.keys(opts.fixed).length > 0) {
    params.set("fixed", JSON.stringify(opts.fixed));
  }
  return apiClient<TrustMapResponse>(`/composite/trust-map?${params.toString()}`);
}

// Per-model fitted surfaces + confidence-weighted fused surface, over a
// (axis_x, axis_y) grid with the other axes held. Arrays are row-major
// (y outer, x inner), length nx*ny — matching trust-map's cell ordering.
export interface SurfaceModel {
  layer_id: string;
  source_class: SourceClass | "gp";
  /** Honest engine label for the "gp" layer: "residual-gp" (prior + GP(residual),
   *  the headline multifidelity surrogate), or the "gp"/"mfk" absolute-HF fallback. */
  engine?: string;
  z: (number | null)[];
  sigma: (number | null)[];
  applicability: number[];
  confidence: number[];
  // ── Residual-GP overlay (present only when engine === "residual-gp", FXC-9009) ──
  /** The flexfoil ROM prior evaluated on the grid (z = prior + residual correction). */
  prior?: (number | null)[];
  /** How many high-fidelity residual points the GP was fit on. */
  n_residual?: number;
  /** RMS of the residuals (HF − prior) the GP modelled — small when the prior is good. */
  residual_rms?: number;
  /** Fraction of the HF lift variance the prior already explains (1 − var(resid)/var(HF)). */
  prior_share?: number;
}

export interface SurfaceSamplePoint {
  x: number;
  y: number;
  value: number;
}

export interface SurfaceResponse {
  quantity: string;
  policy: string;
  axis_x: string;
  axis_y: string;
  x_bounds: [number, number];
  y_bounds: [number, number];
  nx: number;
  ny: number;
  fixed: Record<string, number | string>;
  sharpness: number;
  cutoff: number;
  gp_engine?: string | null;
  models: SurfaceModel[];
  fused: {
    z: (number | null)[];
    sigma: (number | null)[];
    confidence: number[];
    disagreement_z: number[];
  };
  samples: Array<{ source_class: SourceClass; points: SurfaceSamplePoint[] }>;
}

// Fitted trust-overlay surfaces: each source class as its own surface whose
// opacity tracks confidence, plus a fused surface. sharpness (ζ) / cutoff (ξ)
// / userPriors drive the confidence-weighted fusion.
export async function fetchSurface(
  opts: CommonParams & {
    quantity: string;
    axisX?: string;
    axisY?: string;
    nx?: number;
    ny?: number;
    sharpness?: number;
    cutoff?: number;
    useGp?: boolean;
    splitFidelity?: boolean;
    fixed?: Record<string, number | string>;
    userPriors?: Record<string, number>;
    /** Abort seam (react-query passes its own): a superseded slice fetch must
     *  not queue behind the next one on a single-worker host (FXC-9009). */
    signal?: AbortSignal;
  },
): Promise<SurfaceResponse> {
  const params = baseParams(opts);
  params.set("quantity", opts.quantity);
  if (opts.axisX) params.set("axis_x", opts.axisX);
  if (opts.axisY) params.set("axis_y", opts.axisY);
  if (opts.nx != null) params.set("nx", String(opts.nx));
  if (opts.ny != null) params.set("ny", String(opts.ny));
  if (opts.sharpness != null) params.set("sharpness", String(opts.sharpness));
  if (opts.cutoff != null) params.set("cutoff", String(opts.cutoff));
  if (opts.useGp != null) params.set("use_gp", String(opts.useGp));
  if (opts.splitFidelity != null) params.set("split_fidelity", String(opts.splitFidelity));
  if (opts.fixed && Object.keys(opts.fixed).length > 0) params.set("fixed", JSON.stringify(opts.fixed));
  if (opts.userPriors && Object.keys(opts.userPriors).length > 0) params.set("user_priors", JSON.stringify(opts.userPriors));
  return apiClient<SurfaceResponse>(`/composite/surface?${params.toString()}`, { signal: opts.signal });
}

// Baked residual-GP field for CLIENT-SIDE slicing (FXC-9009). The whole N-D
// field ships once; the browser N-linear-interpolates a (x,y) plane locally so
// dragging a held-value slider reslices in real time with NO backend round-trip
// (vs the ~14s /composite/surface grid assembly). `available:false` means no
// baked field — the caller falls back to fetchSurface. The payload contract +
// the pure slicer live in gpField.ts (DOM-free so the slicer unit-tests).
export type { GpField } from "../features/thread-v5/gpField";

export async function fetchGpField(
  opts: Omit<CommonParams, "policy"> & {
    quantity: string;
    /** react-query passes its own; forwarded so a superseded fetch aborts. */
    signal?: AbortSignal;
  },
): Promise<import("../features/thread-v5/gpField").GpField> {
  // gp-field is policy-agnostic (it's the baked residual-GP over the raw HF
  // scatter), so mirror fetchSurface's base params minus policy.
  const params = baseParams({ projectId: opts.projectId, branch: opts.branch });
  params.set("quantity", opts.quantity);
  return apiClient(`/composite/gp-field?${params.toString()}`, { signal: opts.signal });
}

export interface BuildupTerm {
  role: "baseline" | "increment";
  value?: number | null;
  delta?: number | null;
  raw_delta?: number | null;
  sigma?: number | null;
  source_class?: SourceClass;
  dataset_name?: string;
  classification?: string;
  corrected?: boolean;
  axes?: string[];
  note?: string;
  why?: string;
  mode?: "additive" | "scaled" | "calibrated" | "ratio";
  k?: {
    k?: number;
    k_sigma?: number;
    n_pairs?: number;
    residual_rms?: number;
    ratio?: number;
    source: "fixed" | "calibrated" | "ratio";
    artifact_id?: string | null;
  };
  gate?: { cap: number; score: number; verdict: "trusted" | "flagged" | "rejected" };
}

export interface BuildupResult {
  condition_key: string;
  condition: Record<string, number | string>;
  base_condition_key: string;
  quantity: string;
  spec: string;
  baseline_policy: string;
  value: number | null;
  sigma: number | null;
  classification: CompositeClassification;
  terms: BuildupTerm[];
}

export interface BuildupSweepResponse {
  quantity: string;
  axis: string;
  values: number[];
  fixed: Record<string, number | string>;
  spec: string;
  baseline_policy: string;
  composed: BuildupResult[];
  sources: Record<
    string,
    {
      source_class: SourceClass;
      points: Array<{
        value: number;
        sigma: number | null;
        exact: boolean;
        corrected: boolean;
        applicability: string;
        [axis: string]: number | string | boolean | null;
      }>;
    }
  >;
}

// Superposition sweep: baseline absolutes + same-model control deltas
// along one control axis, with every source's raw values for comparison.
export async function fetchBuildupSweep(
  opts: Omit<CommonParams, "policy"> & {
    quantity: string;
    axis?: string;
    lo?: number;
    hi?: number;
    n?: number;
    fixed: Record<string, number | string>;
    spec?: string;
  },
): Promise<BuildupSweepResponse> {
  const params = baseParams({ projectId: opts.projectId, branch: opts.branch });
  params.set("quantity", opts.quantity);
  if (opts.axis) params.set("axis", opts.axis);
  if (opts.lo != null) params.set("lo", String(opts.lo));
  if (opts.hi != null) params.set("hi", String(opts.hi));
  if (opts.n != null) params.set("n", String(opts.n));
  if (opts.spec) params.set("spec", opts.spec);
  params.set("fixed", JSON.stringify(opts.fixed));
  return apiClient<BuildupSweepResponse>(`/composite/buildup-sweep?${params.toString()}`);
}

export interface RunRecommendation {
  condition: Record<string, number | string>;
  condition_key: string;
  purpose: "envelope" | "increment_validation";
  increment_axis?: string;
  score: number;
  components: Record<string, number>;
  reasons: string[];
  classification: CompositeClassification;
  disagreement_z: number;
  suggested_source: "cfd" | "wind_tunnel";
  gp_std?: number;
}

export interface RecommendationsResponse {
  quantity: string;
  policy: string;
  spec: string;
  engine: string;
  budget: number;
  n_candidates_considered: number;
  recommendations: RunRecommendation[];
}

// "What should we run next?" — ranked candidate conditions with reasons.
export async function fetchRecommendations(
  opts: CommonParams & { quantity: string; budget?: number; spec?: string; useGp?: boolean },
): Promise<RecommendationsResponse> {
  const params = baseParams(opts);
  params.set("quantity", opts.quantity);
  if (opts.budget != null) params.set("budget", String(opts.budget));
  if (opts.spec) params.set("spec", opts.spec);
  // FXC-9009: the board's "Run next" tile only needs the heuristic ranking; passing
  // use_gp=false skips the O(n^3) GP surrogate fit (which has no precompute fast-path
  // and pegs the single-worker backend). The explicit "Propose next N" button leaves
  // it default (true) for the full surrogate-variance ranking.
  if (opts.useGp != null) params.set("use_gp", String(opts.useGp));
  return apiClient<RecommendationsResponse>(`/composite/recommendations?${params.toString()}`);
}

export interface WriteResult {
  operation_id: string;
  artifact_id: string;
  deduplicated: string;
}

// Propose a manual correction (status="proposed" until approved).
export async function createCorrection(
  opts: Omit<CommonParams, "policy"> & {
    sourceKey: string;
    conditionKey: string;
    quantity: string;
    action: "replace" | "reject";
    correctedValue?: number;
    reason: string;
  },
): Promise<WriteResult & { correction_id: string; status: string }> {
  const params = baseParams({ projectId: opts.projectId, branch: opts.branch });
  return apiClient(`/composite/corrections?${params.toString()}`, {
    method: "POST",
    body: {
      source_key: opts.sourceKey,
      condition_key: opts.conditionKey,
      quantity: opts.quantity,
      action: opts.action,
      corrected_value: opts.correctedValue,
      reason: opts.reason,
    },
  });
}

export async function approveCorrection(
  opts: Omit<CommonParams, "policy"> & { correctionId: string },
): Promise<{ correction_id: string; status: string }> {
  const params = baseParams({ projectId: opts.projectId, branch: opts.branch });
  return apiClient(`/composite/corrections/approve?${params.toString()}`, {
    method: "POST",
    body: { correction_id: opts.correctionId },
  });
}

// Persist the current comparison plot as a provenance-bearing artifact.
export async function saveComparisonPlot(
  opts: Omit<CommonParams, "policy"> & {
    title: string;
    quantity: string;
    policy: string;
    xAxis: string;
    points: Array<Record<string, unknown>>;
  },
): Promise<WriteResult & { title: string }> {
  const params = baseParams({ projectId: opts.projectId, branch: opts.branch });
  return apiClient(`/composite/save-plot?${params.toString()}`, {
    method: "POST",
    body: { title: opts.title, quantity: opts.quantity, policy: opts.policy,
            x_axis: opts.xAxis, points: opts.points },
  });
}
